import React, { useEffect, useState } from 'react';
import Modal from './ui/Modal';
import apiService from '../services/apiService';
import { useI18n } from '../utils/i18n';

// #118 (W7): „Was ist neu?" zeigt die AKTUELLE Version mit ihrem
// CHANGELOG-Abschnitt; ältere Versionen sind über ein Dropdown wählbar
// (Quelle: release_note_versions, befüllt aus CHANGELOG.md per
// scripts/import_release_notes.js). Fallback auf die alte Prepend-History
// (release_notes), solange noch keine Versionen importiert sind.
// Unabhängig vom Einmal-Overlay auf dem Dashboard.

// Kleiner Renderer für die CHANGELOG-Untermenge: ### Überschriften,
// "- "-Listen, **fett**. Bewusst keine Markdown-Library.
const renderBold = (text) =>
  String(text).split(/\*\*(.+?)\*\*/g).map((part, idx) => (idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part));

const renderChangelog = (text) => {
  const lines = String(text || '').split('\n');
  return lines.map((line, i) => {
    if (line.startsWith('### ')) {
      return <h4 key={i} style={{ margin: '14px 0 6px 0' }}>{line.slice(4)}</h4>;
    }
    if (/^\s*-\s+/.test(line)) {
      return (
        <div key={i} style={{ display: 'flex', gap: 8, margin: '4px 0' }}>
          <span>•</span>
          <span style={{ flex: 1 }}>{renderBold(line.replace(/^\s*-\s+/, ''))}</span>
        </div>
      );
    }
    if (line.trim() === '') {
      return <div key={i} style={{ height: 8 }} />;
    }
    return <p key={i} style={{ margin: '4px 0' }}>{renderBold(line)}</p>;
  });
};

const ReleaseNotesModal = ({ open, onClose }) => {
  const { t, getLocale } = useI18n();
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [detail, setDetail] = useState(null);
  const [legacyNote, setLegacyNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Versionsliste beim Öffnen laden; ohne Import-Stand Fallback auf die History
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLegacyNote(null);
    apiService.getReleaseNoteVersions()
      .then(async (resp) => {
        if (cancelled) return;
        const list = resp?.versions || [];
        setVersions(list);
        if (list.length > 0) {
          setSelectedVersion(list[0].version);
        } else {
          const legacy = await apiService.getReleaseNoteHistory();
          if (!cancelled) setLegacyNote(legacy?.note || null);
        }
      })
      .catch(() => { if (!cancelled) setError(t('releaseNotes.loadError', 'Versions-History konnte nicht geladen werden.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, t]);

  // Inhalt der gewählten Version laden
  useEffect(() => {
    if (!open || !selectedVersion) return undefined;
    let cancelled = false;
    setDetail(null);
    apiService.getReleaseNoteVersion(selectedVersion)
      .then((resp) => { if (!cancelled) setDetail(resp || null); })
      .catch(() => { if (!cancelled) setError(t('releaseNotes.loadError', 'Versions-History konnte nicht geladen werden.')); });
    return () => { cancelled = true; };
  }, [open, selectedVersion, t]);

  const formatReleasedAt = (releasedAt) => {
    if (!releasedAt) return '';
    try {
      return new Date(releasedAt).toLocaleDateString(getLocale ? getLocale() : undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (_) {
      return releasedAt;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('releaseNotes.historyTitle', 'Was ist neu?')}
      maxWidth="640px"
      closeLabel={t('common.close', 'Schließen')}
      footer={(
        <button type="button" className="btn btn-primary" onClick={onClose} style={{ minWidth: 100 }}>
          OK
        </button>
      )}
    >
      {loading && <p className="section-description">{t('common.loading', 'Lade...')}</p>}
      {error && <div className="alert alert-error">{error}</div>}

      {!loading && !error && versions.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <label htmlFor="release-note-version" style={{ fontWeight: 600 }}>
              {t('releaseNotes.versionLabel', 'Version')}
            </label>
            <select
              id="release-note-version"
              className="form-select"
              style={{ maxWidth: 260 }}
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
            >
              {versions.map((v, idx) => (
                <option key={v.version} value={v.version}>
                  v{v.version}
                  {v.releasedAt ? ` – ${formatReleasedAt(v.releasedAt)}` : ''}
                  {idx === 0 ? ` (${t('releaseNotes.current', 'aktuell')})` : ''}
                </option>
              ))}
            </select>
          </div>
          {detail ? (
            <div style={{ lineHeight: 1.6 }}>{renderChangelog(detail.content)}</div>
          ) : (
            <p className="section-description">{t('common.loading', 'Lade...')}</p>
          )}
        </>
      )}

      {/* Fallback: Versionen noch nicht importiert → alte Prepend-History */}
      {!loading && !error && versions.length === 0 && legacyNote && (
        <>
          {legacyNote.title && <h3 style={{ marginTop: 0 }}>{legacyNote.title}</h3>}
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {legacyNote.content}
          </div>
        </>
      )}
      {!loading && !error && versions.length === 0 && !legacyNote && (
        <p className="section-description">{t('releaseNotes.empty', 'Noch keine Releasenotes vorhanden.')}</p>
      )}
    </Modal>
  );
};

export default ReleaseNotesModal;
